import { apiGet } from './client';

// ─────────────────────────────────────────────────────────────────────────────
// Instruction Lint — backed by OO.PromptEngineering.Lint.IAgentInstructionLintService
//
// Returns the agent's authoring-time findings (Grounding, Clinical safety,
// Anti-patterns categories) and a count summary. Pure read endpoint — safe
// for save-time invocation OR background polling from the editor.
//
// Severity scale matches the design vision:
//   • Block (2) — only fired when the agent's EnforceX flag is true. Save UI
//                  should disable the Save button until cleared.
//   • Warn  (1) — yellow advisory card. Operator can dismiss / save anyway.
//   • Info  (0) — blue informational card.
// ─────────────────────────────────────────────────────────────────────────────

export type LintSeverity = 0 | 1 | 2;

export const LINT_SEVERITY = {
  Info: 0 as const,
  Warn: 1 as const,
  Block: 2 as const,
};

/**
 * Severity descriptor — display metadata for a single severity level.
 * Comes back on every finding so the frontend can render the chip popover
 * + the in-drawer scale graphic without a second round-trip.
 */
export interface LintSeverityDescriptor {
  severity: LintSeverity;
  label: string;             // "Blocking" | "Advisory" | "Info"
  shortDescription: string;  // one-line, used in chip popover
  longDescription: string;   // 1-2 sentences, used in rule-detail drawer
  scaleBars: number;         // 3 / 2 / 1 — for the ▮▮▮ scale graphic
}

export interface LintFinding {
  ruleId: string;            // "G-01", "H-02", "H-05.kisunla", ...
  category: string;          // "Grounding" | "Clinical safety" | ...
  title: string;
  severity: LintSeverity;
  message: string;
  /**
   * Pre-rendered Markdown ready to paste at cursor in the instruction editor.
   * Null when the rule has no auto-fix.
   */
  suggestedSnippetMarkdown: string | null;
  /** Linked items the rule called out (e.g. skill names not referenced). */
  missingReferences: string[] | null;
  isClean: boolean;

  // ── Rule-detail metadata (powers the rule-detail drawer) ────────────────
  // Sourced from ILintRule.WhatItChecks / WhyItExists / CompliantSnippetMarkdown
  // on the backend, plus descriptor lookups for category and severity.

  /** One-sentence summary of what this rule checks. */
  whatItChecks: string | null;
  /** Story-style explanation of why this rule exists. Markdown. */
  whyItExists: string | null;
  /** Anonymized example of compliant phrasing. Markdown. */
  compliantSnippetMarkdown: string | null;
  /** One-line description for the category chip's hover popover. */
  categoryDescription: string | null;
  /** Full descriptor for the severity chip + in-drawer scale graphic. */
  severityDescriptor: LintSeverityDescriptor | null;
}

export interface AgentInstructionLintResult {
  agentId: number;
  agentDisplayName: string;
  canonicalName: string | null;
  audience: string | null;
  enforceKbLoad: boolean;
  enforceSkillLoad: boolean;

  linkedSkillCount: number;
  linkedKnowledgeCount: number;
  enabledActionCount: number;

  rulesEvaluated: number;
  rulesFired: number;
  blockingFindings: number;     // count of Severity = Block
  advisoryFindings: number;     // count of Severity = Warn

  /** Findings that fired (clean rules are NOT included). */
  findings: LintFinding[];

  /** True when at least one finding has Severity = Block. */
  hasBlockers: boolean;
}

export async function getAgentInstructionLint(agentId: number): Promise<AgentInstructionLintResult> {
  const result = await apiGet<AgentInstructionLintResult>(`/api/Agent/InstructionLint/${agentId}`);
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Rule Library — platform-wide catalog of every lint rule (no agent context).
// Powers the Platform → Rule Library page (lint-ux-evolution.md §3).
//
// The same WhatItChecks / WhyItExists / CompliantSnippetMarkdown text that
// renders in the rule-detail drawer (on the lint findings page) IS this
// catalog's data. Single source of truth on the backend (ILintRule props).
// ─────────────────────────────────────────────────────────────────────────────

export interface LintRuleCatalogEntry {
  id: string;                    // "G-01", "H-02", ...
  category: string;              // "Grounding" | "Clinical safety" | ...
  categoryDescription: string;   // for the category chip's hover popover
  title: string;
  whatItChecks: string;          // one-sentence summary
  whyItExists: string;           // story-style markdown
  compliantSnippetMarkdown: string;
}

export async function getLintRuleCatalog(): Promise<LintRuleCatalogEntry[]> {
  const result = await apiGet<LintRuleCatalogEntry[]>('/api/Agent/LintRuleCatalog');
  return result ?? [];
}
